import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Observable, of, Subject, firstValueFrom } from 'rxjs';
import { catchError, shareReplay, switchMap, startWith, take } from 'rxjs/operators';

import { UserProfileService } from '../../services/user-profile.service';
import { UserProfile } from '../../models/UserProfile';
import { NameEditDialogComponent, NameEditResult } from '../name-edit-dialog/name-edit-dialog.component';
import {PasswordEditDialogComponent, PasswordEditResult} from '../password-edit-dialog/password-edit-dialog.component';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './profile.html',
  styleUrls: ['./profile.scss']
})
export class Profile implements OnInit {
  private readonly userProfileService = inject(UserProfileService);
  private dialog = inject(MatDialog);

  private refresh$ = new Subject<void>();

  profile$!: Observable<UserProfile | null>;
  error: string | null = null;
  isUpdating = false; // <-- Spinner-Flag

  ngOnInit(): void {
    this.profile$ = this.refresh$.pipe(
      startWith(void 0), // initialer Load
      switchMap(() =>
        this.userProfileService.getUserProfile().pipe(
          catchError(_ => {
            this.error = 'Profil konnte nicht geladen werden.';
            return of(null);
          })
        )
      ),
      shareReplay(1)
    );
  }

  async onChangeName(): Promise<void> {
    const profile = await firstValueFrom(this.profile$.pipe(take(1)));
    if (!profile) return;

    const ref = this.dialog.open(NameEditDialogComponent, {
      width: '420px',
      data: { firstName: profile.firstName, lastName: profile.lastName },
      autoFocus: true,
      restoreFocus: false,
    });

    const result: NameEditResult = await firstValueFrom(ref.afterClosed().pipe(take(1)));
    if (!result) return;

    this.isUpdating = true;
    try {
      // 1) Backend speichern
      await firstValueFrom(this.userProfileService.updateUserName(result));
      // 2) Neu laden und warten, bis der neue Wert da ist
      this.refresh$.next();
      await firstValueFrom(this.profile$.pipe(take(1)));
    } finally {
      this.isUpdating = false;
    }
  }

  async onChangePassword(): Promise<void> {
    const ref = this.dialog.open(PasswordEditDialogComponent, { width: '420px' });
    const result: PasswordEditResult = await firstValueFrom(ref.afterClosed().pipe(take(1)));
    if (!result) return;

    this.isUpdating = true;
    this.error = null;
    try {
      await firstValueFrom(this.userProfileService.changePassword(result));
      // Optional: Erfolgsmeldung anzeigen (SnackBar/Toast) – falls vorhanden
    } catch {
      this.error = 'Passwort konnte nicht geändert werden.';
    } finally {
      this.isUpdating = false;
    }
  }
}
